import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { LoaderCircle, Trash2 } from 'lucide-react'
import { apiRequest, getErrorMessage } from '@/lib/api'

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogMedia,
    AlertDialogTitle
} from '@/components/ui/alert-dialog'

export type DeleteTarget = {
    id: string
    name: string
}

interface DeleteProductDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    product: DeleteTarget | null
    onDeleted: (product: DeleteTarget) => void
}

const DeleteProductDialog: FC<DeleteProductDialogProps> = ({
    open,
    onOpenChange,
    product,
    onDeleted
}): ReactNode => {
    const [deleting, setDeleting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const handleDelete = async () => {
        if (!product) return

        setDeleting(true)
        setError(null)

        try {
            await apiRequest(`/api/products/${product.id}`, { method: 'DELETE' })
            onDeleted(product)
        }
        catch (deleteError) {
            setError(getErrorMessage(deleteError))
        }
        finally {
            setDeleting(false)
        }
    }

    return (
        <AlertDialog
            open={open}
            onOpenChange={nextOpen => {
                if (deleting) return

                onOpenChange(nextOpen)
            }}
            onOpenChangeComplete={nextOpen => {
                if (!nextOpen) setError(null)
            }}
        >
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogMedia className='bg-danger-soft text-danger-ink'>
                        <Trash2 aria-hidden='true' />
                    </AlertDialogMedia>

                    <AlertDialogTitle>
                        ¿Eliminar este producto?
                    </AlertDialogTitle>

                    <AlertDialogDescription>
                        {product ? (
                            <>
                                Se borrará «<span data-mdlfy-cms={`products:${product.id}:name`}>{product.name}</span>» junto con todo su historial de movimientos. Esta acción no se puede deshacer.
                            </>
                        ) : 'Esta acción no se puede deshacer.'}
                    </AlertDialogDescription>
                </AlertDialogHeader>

                {error && (
                    <p role='alert' className='rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger-ink'>
                        {error}
                    </p>
                )}

                <AlertDialogFooter>
                    <AlertDialogCancel disabled={deleting}>
                        Cancelar
                    </AlertDialogCancel>

                    <AlertDialogAction
                        onClick={handleDelete}
                        disabled={deleting}
                        className='bg-destructive text-white hover:bg-destructive/90'
                    >
                        {deleting && <LoaderCircle className='animate-spin' aria-hidden='true' />}
                        {deleting ? 'Eliminando…' : 'Eliminar producto'}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}

export default DeleteProductDialog
